// 📄 components/credenciales/HistorialCredencial.tsx
// Historial de "Obtener credenciales" (docs/plans/plan-revelado-credenciales.md, R4):
// quién pidió, aprobó, rechazó y vio cada contraseña, cambios de aprobadores
// y permisos. El backend lo guarda solo agregando (no se edita ni se borra)
// y nunca con valores. Lo ven owner/admin, quien la guardó, los aprobadores
// y el usuario cliente (la información es suya); el general de la
// organización, solo owner/admin.
import React, { useCallback, useEffect, useState } from 'react';
import { History, RefreshCw, X } from 'lucide-react';
import { CredencialRef, EventoHistorial, historialCredencial, historialOrganizacion } from '../../services/credencialAccesoService.ts';

const EVENTOS: Record<string, { label: string; clase: string }> = {
  'credencial.guardada': { label: 'Guardó una contraseña nueva', clase: 'text-blue-300' },
  'politica.creada': { label: 'Definió los aprobadores', clase: 'text-gray-300' },
  'politica.cambiada': { label: 'Cambió los aprobadores', clase: 'text-gray-300' },
  'politica.reasignada': { label: 'Reasignó los aprobadores (owner/admin)', clase: 'text-indigo-300' },
  'solicitud.creada': { label: 'Pidió la contraseña', clase: 'text-amber-300' },
  'solicitud.aprobada': { label: 'Aprobó la solicitud', clase: 'text-emerald-400' },
  'solicitud.rechazada': { label: 'Rechazó la solicitud', clase: 'text-red-400' },
  'solicitud.cancelada': { label: 'Se canceló la solicitud', clase: 'text-gray-400' },
  'solicitud.expirada': { label: 'La solicitud venció', clase: 'text-gray-400' },
  revelado: { label: 'Vio la contraseña (con aprobación)', clase: 'text-amber-300' },
  'revelado.autoaprobado': { label: 'Vio la contraseña (la había guardado)', clase: 'text-amber-300' },
  'revelado.fallido': { label: 'Intentó verla y falló el descifrado', clase: 'text-red-400' },
  'permiso.otorgado': { label: 'Dio el permiso "Obtener credenciales"', clase: 'text-blue-300' },
  'permiso.revocado': { label: 'Quitó el permiso "Obtener credenciales"', clase: 'text-gray-300' }
};

const detalleTexto = (e: EventoHistorial) => {
  const d = e.detalle || {};
  const partes: string[] = [];
  if (d.motivo) partes.push(`Motivo: ${d.motivo}`);
  if (d.comentario) partes.push(`Comentario: ${d.comentario}`);
  if (d.despues) {
    const n = d.despues.aprobadores?.length || 0;
    partes.push(n ? `${n} aprobador(es), ${d.despues.modo === 'todos' ? 'aprueban todos' : 'basta con uno'}` : 'sin aprobadores (aprueba quien la guardó)');
  }
  if (d.miembroUid) partes.push(`Miembro: ${d.miembroNombre || d.miembroUid}`);
  return partes.join(' · ');
};

export function ListaHistorial({ cargar, mostrarCredencial = false }: {
  cargar: (despuesDe?: string) => Promise<{ eventos: EventoHistorial[]; siguiente: string | null }>;
  mostrarCredencial?: boolean;
}) {
  const [eventos, setEventos] = useState<EventoHistorial[]>([]);
  const [siguiente, setSiguiente] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pedir = useCallback(async (despuesDe?: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await cargar(despuesDe);
      setEventos((prev) => (despuesDe ? [...prev, ...r.eventos] : r.eventos));
      setSiguiente(r.siguiente);
    } catch (err: any) {
      setError(err.message || 'No se pudo cargar el historial.');
    } finally {
      setLoading(false);
    }
  }, [cargar]);

  useEffect(() => { pedir(); }, [pedir]);

  return (
    <div className="space-y-2">
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
      {!loading && !error && eventos.length === 0 && <p className="text-sm text-gray-500 text-center py-6">Todavía no hay movimientos.</p>}
      <ul className="divide-y divide-white/5">
        {eventos.map((e) => {
          const meta = EVENTOS[e.tipoEvento] || { label: e.tipoEvento, clase: 'text-gray-300' };
          const detalle = detalleTexto(e);
          return (
            <li key={e.id} className="py-2.5 text-xs space-y-0.5">
              <div className="flex items-start justify-between gap-3">
                <p><span className="text-gray-200 font-medium">{e.actorNombre || e.actorUid}</span> <span className={meta.clase}>{meta.label.toLowerCase()}</span></p>
                <span className="text-gray-500 whitespace-nowrap">{e.en ? new Date(e.en).toLocaleString() : ''}</span>
              </div>
              {mostrarCredencial && <p className="text-gray-400">{e.etiqueta}</p>}
              {detalle && <p className="text-gray-500 break-words">{detalle}</p>}
              {e.ip && <p className="text-[10px] text-gray-600">IP {e.ip}</p>}
            </li>
          );
        })}
      </ul>
      {loading && (
        <div className="flex items-center justify-center gap-2 text-xs text-gray-500 py-3">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Cargando historial...
        </div>
      )}
      {!loading && siguiente && (
        <div className="flex justify-center">
          <button type="button" onClick={() => pedir(siguiente)} className="px-3 py-1.5 rounded-lg text-xs text-gray-300 bg-white/5 hover:bg-white/10">Ver más</button>
        </div>
      )}
    </div>
  );
}

export function HistorialCredencialModal({ credencial, titulo, onClose }: { credencial: CredencialRef; titulo: string; onClose: () => void }) {
  const cargar = useCallback(
    (despuesDe?: string) => historialCredencial(credencial, despuesDe),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [credencial.tipo, credencial.clienteId, credencial.apiId, credencial.projectId, credencial.credId]
  );
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-base font-medium text-white truncate flex items-center gap-2"><History className="w-4 h-4 text-blue-400" /> Historial · {titulo}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          <ListaHistorial cargar={cargar} />
        </div>
      </div>
    </div>
  );
}

/** Historial general de una organización (solo owner/admin; lo valida el backend). */
export function HistorialOrganizacion({ orgId }: { orgId: string }) {
  const cargar = useCallback((despuesDe?: string) => historialOrganizacion(orgId, despuesDe), [orgId]);
  return <ListaHistorial cargar={cargar} mostrarCredencial />;
}
